// What a metric selector lists and what a click on it does, worked out away from the control so
// the two selectors -- the grid table's and a sub-question's -- share one rule book.
//
// Every metric is independent: the control offers checkboxes, and checkboxes promise that each
// stands alone. The one governing row differs by what the selector governs. A chart has answer
// columns to hide, so its row is All answers; a table is the metrics, so its row is All metrics --
// switching the whole set is a table's most useful single act and rarely what a chart wants.
//
// Spec: specs/stats/questions/summary_metrics/functional_requirements.md §6.1

import { metricByKey, normaliseSelection } from '@aytm/app/stats/page/question-views/mx/lib/summary/registry'

export const ALL_ANSWERS = 'all'
export const ALL_METRICS = 'allMetrics'

const ALL_ANSWERS_TEXT = 'All answers'
// named for what it governs: on a sub-question it sits one row from All answers, and two rows both
// starting "All" that govern different things is a reading nobody should have to do twice
const ALL_METRICS_TEXT = 'All metrics'

// what a metric the chart's value axis cannot carry says about itself, before it is picked rather
// than after -- worth saying only where the rest of the list does reach a chart
const FIGURE_ONLY_NOTE = 'table only'

// Hiding the answers with nothing in their place would leave an empty plot, so the row only opens
// up once a metric that draws a column is on -- a figure-only metric leaves the chart untouched.
const canHideAnswers = (items, keys) => items.some(item => item.plottable && keys.includes(item.key))

const allOn = (items, keys) => 0 < items.length && items.every(item => keys.includes(item.key))

export const calcSelectorOptions = ({ metrics, selection, governsChart = true } = {}) => {
  const items = metrics?.items || []
  const keys = selection?.keys || []

  const governing = governsChart
    ? {
        key: ALL_ANSWERS,
        text: ALL_ANSWERS_TEXT,
        checked: false !== selection?.allAnswers,
        disabled: !canHideAnswers(items, keys)
      }
    : // marked when the whole list is on, so it reads as the state of the list rather than as a button
      { key: ALL_METRICS, text: ALL_METRICS_TEXT, checked: allOn(items, keys), disabled: false }

  return [
    governing,
    ...items.map((item, index) => ({
      key: item.key,
      text: item.label,
      checked: keys.includes(item.key),
      disabled: false,
      note: governsChart && !item.plottable ? FIGURE_ONLY_NOTE : null,
      // a rule under the governing row, and one wherever the family changes: a box answers "how
      // many are at this end" and a mean "where the whole sits"
      divided: 0 === index || item.family !== items[index - 1].family
    }))
  ]
}

export const applySelectorChange = ({ metrics, selection, governsChart = true } = {}, touched) => {
  const items = metrics?.items || []
  const current = { keys: selection?.keys || [], allAnswers: false !== selection?.allAnswers }

  if (ALL_METRICS === touched) {
    return { keys: allOn(items, current.keys) ? [] : items.map(item => item.key), allAnswers: true }
  }

  if (ALL_ANSWERS === touched) {
    if (!governsChart || !canHideAnswers(items, current.keys)) return selection

    return { ...current, allAnswers: !current.allAnswers }
  }

  if (null === metricByKey(metrics, touched)) return selection

  const keys = normaliseSelection(
    current.keys.includes(touched) ? current.keys.filter(key => key !== touched) : [...current.keys, touched],
    metrics
  )

  // clearing the last box metric restores the answers: a chart with neither has nothing to draw
  return { keys, allAnswers: current.allAnswers || !canHideAnswers(items, keys) }
}
